import React from 'react';
import WhiteSquare from '../assets/images/white-square.png';
import ArrowUpIcon from '../assets/icons/arrow-up-white.svg';
import CloseIcon from '../assets/icons/close.svg';
import DisclaimerInfoIcon from '../assets/icons/disclaimer-info.svg';
import { useIsMobileScreenSize } from '../hooks/use-is-mobile-screen-size';
import { Button } from './Button';
import { Form } from './Form';
import { CtaProps } from './LegacyCta';
import { PoweredBySniply } from './PoweredBySniply';
import { TextLink } from './TextLink';

export const LegacyCtaSocial: React.FC<CtaProps> = ({
    configuration: {
        type,
        headlineText,
        imageUrl,
        urlToBePromoted,
        backgroundColor,
        messageText,
        messageTextColor,
        buttonColor,
        buttonTextColor,
        buttonText,
        formButtonColor,
        formButtonTextColor,
        formPlaceholderText,
        linkText,
        linkTextColor,
        position,
        showSniplyLogo,
        showWhiteSniplyLogo
    },
    disclaimerHref,
    onSubmitFormInputValue,
    onConvertClick,
    isDemoMobileScreen = false
}) => {
    const [hide, setHide] = React.useState(false);

    const isMobileScreen = useIsMobileScreenSize();
    const showMobileView = isDemoMobileScreen || isMobileScreen;

    return hide ? (
        <div
            className={`absolute bg-white rounded-full ${
                position === 'bottom-left'
                    ? 'bottom-2 left-2'
                    : position === 'bottom-right'
                      ? 'bottom-2 right-2'
                      : position === 'top-left'
                        ? 'top-2 left-2'
                        : 'top-2 right-2'
            } ${
                position === 'bottom-left'
                    ? 'shadow-cta-b-l'
                    : position === 'bottom-right'
                      ? 'shadow-cta-b-r'
                      : position === 'top-left'
                        ? 'shadow-cta-t-l'
                        : 'shadow-cta-t-r'
            }`}
        >
            <div
                style={{ background: backgroundColor }}
                className="relative p-2 w-full h-full rounded-full"
            >
                <img
                    src={imageUrl || WhiteSquare}
                    className="w-20 h-20 rounded-full object-cover"
                />
                <button
                    className="p-1 absolute top-0.5 right-0.5 bg-primary flex items-center justify-center rounded-full"
                    onClick={() => setHide(false)}
                >
                    <img src={ArrowUpIcon} alt="arrow icon" className="h-4 w-4" />
                </button>
            </div>
        </div>
    ) : showMobileView ? (
        <div
            className={`font-poppins absolute w-19/20 h-52 rounded-md ${
                position === 'bottom-left'
                    ? 'bottom-2 left-2'
                    : position === 'bottom-right'
                      ? 'bottom-2 right-2'
                      : position === 'top-left'
                        ? 'top-12 left-2'
                        : 'top-12 right-2'
            }`}
        >
            <div className="relative">
                <img
                    src={imageUrl || WhiteSquare}
                    className="absolute -top-10 left-2 p-2 rounded-full bg-white text-center border h-20 w-20 object-cover"
                />
                <div
                    style={{ background: backgroundColor }}
                    className={`p-3 flex flex-col w-full h-52 rounded-md ${
                        position === 'bottom-left'
                            ? 'shadow-cta-b-l'
                            : position === 'bottom-right'
                              ? 'shadow-cta-b-r'
                              : position === 'top-left'
                                ? 'shadow-cta-t-l'
                                : 'shadow-cta-t-r'
                    }`}
                >
                    <div className="flex items-start justify-end shrink-0">
                        {showSniplyLogo && (
                            <PoweredBySniply
                                backgroundColor={showWhiteSniplyLogo ? '#2681DB' : backgroundColor}
                            />
                        )}
                        <a
                            className="ml-4 shrink-0"
                            href={disclaimerHref}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img
                                src={DisclaimerInfoIcon}
                                alt="dislaimer info icon"
                                className="h-4 w-4"
                            />
                        </a>
                        <button className="ml-2 shrink-0" onClick={() => setHide(true)}>
                            <img src={CloseIcon} alt="close icon" className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="mt-6 flex items-start justify-between">
                        <p className="text-grey-light text-sm sm:text-base line-clamp-1">
                            {headlineText}
                        </p>
                    </div>
                    <div>
                        <p
                            className="mt-2 text-sm pr-2 line-clamp-3"
                            style={{ color: messageTextColor }}
                        >
                            {messageText}
                        </p>
                    </div>
                    <div className="mt-3">
                        {type === 'text-link' ? (
                            <div className="h-9">
                                <TextLink
                                    href={urlToBePromoted}
                                    linkText={linkText}
                                    linkTextColor={linkTextColor}
                                    onConvertClick={onConvertClick}
                                />
                            </div>
                        ) : type === 'form' ? (
                            <Form
                                buttonColor={formButtonColor}
                                buttonTextColor={formButtonTextColor}
                                placeholderText={formPlaceholderText}
                                onSubmitFormInputValue={onSubmitFormInputValue}
                                onConvertClick={onConvertClick}
                            />
                        ) : (
                            <Button
                                href={urlToBePromoted}
                                buttonColor={buttonColor}
                                buttonText={buttonText}
                                buttonTextColor={buttonTextColor}
                                onConvertClick={onConvertClick}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div
            className={`font-poppins absolute w-120 h-44 rounded-md ${
                position === 'bottom-left'
                    ? 'bottom-2 left-2'
                    : position === 'bottom-right'
                      ? 'bottom-2 right-18'
                      : position === 'top-left'
                        ? 'top-2 left-2'
                        : 'top-2 right-18'
            }`}
        >
            <div className="relative">
                <img
                    src={imageUrl || WhiteSquare}
                    className="absolute top-6 left-0 p-2 w-32 h-32 rounded-full bg-white text-center border object-cover"
                />
                <div
                    style={{ background: backgroundColor }}
                    className={`ml-16 pl-20 pr-2 py-1 flex flex-col w-full h-44 rounded-md ${
                        position === 'bottom-left'
                            ? 'shadow-cta-b-l'
                            : position === 'bottom-right'
                              ? 'shadow-cta-b-r'
                              : position === 'top-left'
                                ? 'shadow-cta-t-l'
                                : 'shadow-cta-t-r'
                    }`}
                >
                    <div className="mt-2 flex items-start justify-between">
                        <p className="text-grey-light text-sm sm:text-base line-clamp-1">
                            {headlineText}
                        </p>
                        <div className="flex items-start justify-end shrink-0">
                            <a
                                className="ml-4 shrink-0"
                                href={disclaimerHref}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <img
                                    src={DisclaimerInfoIcon}
                                    alt="dislaimer info icon"
                                    className="h-4 w-4"
                                />
                            </a>
                            <button className="ml-2 shrink-0" onClick={() => setHide(true)}>
                                <img src={CloseIcon} alt="close icon" className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div>
                        <p
                            className="mt-2 text-sm lg:text-base pr-2 line-clamp-3"
                            style={{ color: messageTextColor }}
                        >
                            {messageText}
                        </p>
                    </div>
                    <div className="mt-3">
                        {type === 'text-link' ? (
                            <div className="h-9">
                                <TextLink
                                    href={urlToBePromoted}
                                    linkText={linkText}
                                    linkTextColor={linkTextColor}
                                    onConvertClick={onConvertClick}
                                />
                            </div>
                        ) : type === 'form' ? (
                            <Form
                                buttonColor={formButtonColor}
                                buttonTextColor={formButtonTextColor}
                                placeholderText={formPlaceholderText}
                                onSubmitFormInputValue={onSubmitFormInputValue}
                                onConvertClick={onConvertClick}
                            />
                        ) : (
                            <Button
                                href={urlToBePromoted}
                                buttonColor={buttonColor}
                                buttonText={buttonText}
                                buttonTextColor={buttonTextColor}
                                onConvertClick={onConvertClick}
                            />
                        )}
                    </div>
                    {showSniplyLogo && (
                        <div className="mt-2 h-full flex items-end justify-end">
                            <PoweredBySniply
                                backgroundColor={showWhiteSniplyLogo ? '#2681DB' : backgroundColor}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
